/** @module csi/widget/DateRangePopup */
define([
	"dojo/_base/declare",
	"dojo/aspect",
	"dojo/date/stamp",
	"dojo/dom-class",
	"dojo/has",
	"dojo/query",
	"dojo/store/Memory",
	"dojo/when",
	"dojo/window",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/focus",
	"dijit/form/_TextBoxMixin",
	"dijit/MenuItem",
	"dijit/MenuSeparator",
	"dgrid/OnDemandList",
	"dgrid/Selection",
	"csi",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/types",
	"./DateRangeMenu",
	"dojo/text!./DateRangePopup.html",

	"./DateRangeCalendar",
	"./DateTextBox",
	"./TimeTextBox"
], function(declare, aspect, stamp, domClass, has, query, Memory, when, win, _TemplatedMixin, _WidgetBase, _WidgetsInTemplateMixin, focus, _TextBoxMixin, MenuItem, MenuSeparator, OnDemandList, ListSelection, csi, _, html, locale, types, DateRangeMenu, template) {

	var DAYS_DISPLAYED_IN_CALENDAR = 42; // 6 weeks
	var DAYS_DISPLAYED_IN_LIST = 14;
	var TIME_INCREMENT = 30;
	var Selection = types.DateRangeSelection;
	var _store;

	/**
	 * Compare two date objects
	 * @param {Date} dt1
	 * @param {Date} dt2
	 * @return {boolean}
	 */
	function _compare(dt1, dt2) {
		if (!dt1 && !dt2) {
			return true;
		}

		return dt1 && dt2 && (dt1.getTime() == dt2.getTime());
	}

	/**
	 * Return date with time set to start of day
	 * @return {Date}
	 */
	function _getDefaultStartTime() {
		// see _TimePicker
		return stamp.fromISOString("T00:00:00");
	}

	/**
	 * Return date with time set to end of day
	 * @return {Date}
	 */
	function _getDefaultEndTime() {
		// see _TimePicker
		return stamp.fromISOString("T23:59:59");
	}

	/**
	 * Return store for menu options
	 */
	function _getStore() {
		if (!_store) {
			var data = [];

			function add(id, res) {
				var obj = {
					id: id,
					name: res ? _.escape(locale.resource(res)) : "",
					cls: res ? MenuItem : MenuSeparator
				};

				data.push(obj);
				return obj;
			}

			add(Selection.ALL, "SelectAll");
			add("Sep1");
			add(Selection.NONBLANKS, "NonBlanks").blanks = true;
			add(Selection.BLANKS, "Blanks").blanks = true;
			add("Sep2").blanks = true;
			add(Selection.TODAY, "DateRangeToday");
			add(Selection.PAST_24_HOURS, "DateRangePast24Hours").past = true;
			add(Selection.PAST_WEEK, "DateRangePastWeek").past = true;
			add(Selection.PAST_MONTH, "DateRangePastMonth").past = true;
			add(Selection.CUSTOM, "DateRangeCustom");

			_store = new Memory({data: data});
		}

		return _store;
	}

	/**
	 * Sync menu selection with date range selection
	 * @param {Menu} menu
	 * @param {DateRangeSelection} selection
	 */
	function _updateMenuSelection(menu, selection) {
		if (menu) {
			menu.set("selected", _.findWhere(menu.getChildren(), {selection: selection}));
		}
	}

	/**
	 * Sync menu selection with date range selection
	 * @param {Menu} menu
	 * @param {DateRangeSelection} selection
	 */
	function _updateMenuVisibility(menu, prop, bShow) {
		if (menu) {
			_.each(menu.getChildren(), function(menuItem) {
				var item = _store.get(menuItem.selection);
				if (item[prop]) {
					html.setVisible(menuItem, bShow);
				}
			});
		}
	}

	/**
	 * List class showing existing date values
	 */
	var _List = declare([OnDemandList, ListSelection], {
		selectionMode: "range",
		_dateRangePreviousValue: null,

		/**
		 * Extension point called when a new selection is started
		 * @param {Any} value
		 */
		onSelectNew: function(value) { },

		/**
		 * Extension point called when the start of a selection range changes
		 * @param {Any} value
		 */
		onSelectRangeStart: function(value) { },

		/**
		 * Extension point called when the end of a selection range changes
		 * @param {Any} value
		 */
		onSelectRangeEnd: function(value) { },

		/**
		 * Manage _dateRangePreviousValue when clearing selections
		 * @param {string} exceptId
		 * @param {boolean} [dontResetLastSelected]
		 */
		clearSelection: function(exceptId, dontResetLastSelected) {
			this.inherited(arguments);

			if (!dontResetLastSelected) {
				this._dateRangePreviousValue = null;
			}
		},

		/**
		 * Render label with count
		 * @return {DOMNode}
		 */
		renderRow: function(item) {
			var label = this.store.labelWithCountProperty || this.store.labelProperty || "name";
			return html.create("div", {innerText: item[label]});
		},

		/**
		 * Override Selection function to select all items currently rendered in range rather than error out when element is not found
		 */
		select: function(row, toRow, value) {
			var self = this;
			var domNode = this.domNode;
			var store = this.store;
			var from;
			var to;
			var rows;

			function check(obj, bTo) {
				obj = self.row(obj);
				if (obj.element) {
					return obj;
				}

				// find first or last displayed row in range
				rows = rows || query(".dgrid-row", domNode);

				var n = rows.length;
				if (!n) {
					return null;
				}

				obj = self.row(rows[bTo ? --n : 0]);

				var value = store.getIdentity(obj.data);
				if ((from <= value) && (value <= to)) {
					return obj;
				}

				return null;
			}

			if (row && toRow && !row.element && !toRow.element) {
				var itemFrom = row.data || store.get(row) || row;
				var itemTo = toRow.data || store.get(toRow) || toRow;

				from = store.getIdentity(itemFrom);
				to = store.getIdentity(itemTo);

				if (from && to) {
					row = check(row);
					toRow = check(toRow, true);
				}
			}

			if (row) {
				this.inherited(arguments, [row, toRow, value]);
			}
		},

		/**
		 * Handle range selections where every 2 clicks designates a range, even if on the same item
		 * @param {Event} event
		 * @param {DOMNode} target
		 */
		_rangeSelectionHandler: function(event, target) {
			var idProperty = this.store.idProperty;
			var newValue = this.row(target).data[idProperty];
			var lastValue = this._dateRangePreviousValue;
			if (!lastValue) {
				// nothing selected (or previous selection was finished), so select new row
				this.clearSelection();
				this.select(target);
				this._dateRangePreviousValue = newValue;
				this.onSelectNew(newValue);
			} else if (newValue == lastValue) {
				// if same date selected, then set _dateRangePreviousValue to null so next selection will pick new date rather than range
				this._dateRangePreviousValue = null;
			} else {
				// a different date is selected, so select range
				var from = Math.min(lastValue, newValue);
				var to = Math.max(lastValue, newValue);

				this.select(from, to);
				this._dateRangePreviousValue = null;

				if (newValue == from) {
					this.onSelectRangeStart(newValue);
				} else {
					this.onSelectRangeEnd(newValue);
				}
			}
		}
	});

	return declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin], {
		baseClass: "csi-date-range-popup",
		templateString: template,
		DRS: types.DateRangeSelection,

		/** @property {boolean} allowBlanks - set to false to disallow selection of blank and non-blank options */
		allowBlanks: true,

		/** @property {boolean} allowTime - set to false to disallow selection of time range when choosing single day range */
		allowTime: true,

		/** @property {boolean} bBatchFilter - true if dateRangePopup if part of batch filter */
		//bBatchFilter: false,

		/** @property {boolean} inPast - set to true to only allow selection of dates in the past */
		inPast: false,

		/** @property {Date} dateStart - start date of range */
		dateStart: null,

		/** @property {Date} dateEnd - end date of range */
		dateEnd: null,

		/** @property {Object} query - filters for store */
		query: null,

		/** @property {Store} store - store to use to display counts */
		store: null,

		/** @property {DateRangeSelection} selection=CUSTOM - selection type for range, which could specify to select a single day to a date range */
		selection: Selection.ALL,

		/** @property {_DateTimeTextBox.__Constraints} timeConstraints - constraints for time selection */
		timeConstraints: null,

		/** @property {Date} timeStart - start time of range */
		timeStart: null,

		/** @property {Date} timeEnd - end time of range */
		timeEnd: null,

		/** @property {number[]} value - array of three values, the first two are long numbers representing the date range, while the third is the selection value */
		value: null,

		/** @property {boolean} _bGroupChange - indicates if multiple values are being updated at once, so updates to value should wait until later */
		_bGroupChange: false,

		/** @property {Calendar} _calendar - calendar widget to select dates */
		_calendar: null,

		/** @property {DOMNode} _calendarContentNode - node containing content for calendar tab */
		_calendarContentNode: null,

		/** @property {DOMNode} _customDatesNode - node containing date text boxes that are displayed when selecting custom date range */
		_customDatesNode: null,

		/** @property {OnDemandList} _list - list holding existing date values */
		_list: null,

		/** @property {DOMNode} _listContentNode - node containing content for list tab */
		_listContentNode: null,

		/** @property {boolean} _listRendering - if true, then list in process of rendering */
		_listRendering: false,

		/** @property {DateRangeMenu} _menu - range selection menu */
		_menu: null,

		/** @property {DOMNode} _menuNode - container node for menu */
		_menuNode: null,

		/** @property {boolean} _onChangeActive - indicates that widget should call onChange extension point when value changes */
		_onChangeActive: false,

		/** @property {DateRangeCustomTextBox} _startDateBox - date box that displays custom start date */
		_startDateBox: null,

		/** @property {DateRangeCustomTextBox} _endDateBox - date box that displays custom end date */
		_endDateBox: null,

		/** @property {TimeTextBox} _startTimeBox - time box to select start time; only enabled when selecting specific date */
		_startTimeBox: null,

		/** @property {TimeTextBox} _endTimeBox - time box to select end time; only enabled when selecting specific date */
		_endTimeBox: null,

		/**
		 * Set default value and enable change notifications
		 */
		postCreate: function() {
			var self = this;

			this.inherited(arguments);

			// watch for changes to calendar focus so list can be updated with current month's values
			this._calendar.watch("currentFocus", function(attr, oldValue, newValue) {
				_.defer(function() {
					self._updateListPosition();
				});
			});

			// initialize menu
			var menu = this._menu = new DateRangeMenu();

			_getStore().query().forEach(function(item) {
				menu.addChild(new item.cls({
					label: item.name,
					selection: item.id
				}));
			}, this);

			menu.placeAt(this._menuNode, "first");
			menu.startup();
			menu.watch("selected", function(attr, oldValue, newValue) {
				if (newValue) {
					self.set("selection", newValue.selection);
				}
			});

			// sync menu with current settings
			_updateMenuSelection(menu, this.selection);
			_updateMenuVisibility(menu, "blanks", this.allowBlanks);
			_updateMenuVisibility(menu, "past", this.inPast);

			// set constraints for date boxes and remove ability to popup their own calendar
			var dateConstraints = types.getTimeFormatOptions(types.FORMAT_DATE);
			this._startDateBox.set("constraints", dateConstraints);
			this._startDateBox.openDropDown = function() { };
			this._endDateBox.set("constraints", dateConstraints);
			this._endDateBox.openDropDown = function() { };

			// set constraints for time boxes, also ensuring end time cannot be midnight
			var startTimeConstraints = types.getTimeFormatOptions(types.FORMAT_TIME);
			var endTimeConstraints = _.extend(_.clone(startTimeConstraints), {pickerMin: "T00:30:00"});

			this._startTimeBox.set("constraints", startTimeConstraints);
			this._endTimeBox.set("constraints", endTimeConstraints);

			// now that widget is created, notify others of changes to value
			this._onChangeActive = true;
		},

		/**
		 * Extension point called when widget value changes
		 * @param {number[]} value
		 */
		onChange: function(value) { },

		/**
		 * Initialize drop down when opened
		 */
		onOpen: function() {
			// update text boxes to make sure they are in sync with current values - they could have been edited to contain invalid data
			this._startDateBox.set("value", this.dateStart);
			this._endDateBox.set("value", this.dateEnd);
			this._startTimeBox.set("value", this.timeStart);
			this._endTimeBox.set("value", this.timeEnd);

			// force calendar to redraw to make sure _isDisabledDate is in place
			this._updateFocus();

			// focus on menu item for current selection
			var selected = this._menu.get("selected");
			if (selected) {
				this._menu.focusChild(selected);
			}

			// freeze height so that List height is same as calendar height if showing as separate tab
			var h = this._calendarContentNode.offsetHeight;
			if (h) {
				html.setPixels(this._listContentNode, html.HEIGHT, h);
			}

			this._updateList();
		},

		/**
		 * Handle tabbing between date and time widgets; this is necessary because tabbing is normally cut off by Dojo in popups (see dijit/popup#open)
		 * @param {boolean} bShift - indicates if shift key is pressed
		 */
		onTab: function(bShift) {
			var curNode = focus.get("curNode");
			var widgets = [];

			if (this.selection == Selection.CUSTOM) {
				widgets.push(this._startDateBox);
				widgets.push(this._endDateBox);
			}

			if (widgets.length || !this._startTimeBox.get("disabled")) {
				widgets.push(this._startTimeBox);
				widgets.push(this._endTimeBox);
			}

			// if nowhere to go, then return
			if (!widgets.length) {
				return;
			}

			// find index of currently focused widget
			var index = csi.locate(widgets, function(w) {
				return w.focusNode == curNode;
			});

			function move() {
				// increment index in appropriate direction
				if (index == -1) {
					index = bShift ? 3 : 0;
				} else {
					index = (widgets.length + index + (bShift ? -1 : 1)) % widgets.length;
				}

				widgets[index].focus();

				_.defer(function(self) {
					// changing focus could cause disabled status of widgets to change, so tab again if now disabled
					if (widgets[index].get("disabled")) {
						move();
					} else {
						_TextBoxMixin.selectInputText(widgets[index].focusNode);
					}
				}, this);
			}

			move();
		},

		/**
		 * Return true if start and end dates are the same or if past 24 hours selection
		 * @return {boolean}
		 */
		_allowTime: function() {
			if (this.selection == types.DateRangeSelection.PAST_24_HOURS || this.selection == types.DateRangeSelection.TODAY) {
				return true;
			}

			return this._enableTime();
		},

		/**
	   * Return true if start and end dates are the same
	   * @return {boolean}
	   */
		_enableTime: function() {
			return this.dateStart && _compare(types.getStartOfDay(this.dateStart), types.getStartOfDay(this.dateEnd));
		},

		/**
		 * Invalidate dates in the future if so configured
		 * @param {Date} dt
		 * @param {string} [locale]
		 * @return {boolean}
		 */
		_isDisabledDate: function(dt, locale) {
			if (this.inPast) {
				return dt.getTime() >= types.getStartOfDay().setHours(24);
			}

			return false;
		},

		/**
		 * Call onChange extension point if value has changed and widget not restricting notifications; if in popup, then
		 * do not trigger onChange; otherwise popup will be closed immediately after any change
		 * @param {number[]} oldValue
		 */
		_notifyOfChange: function(oldValue) {
			if (this._onChangeActive && !this._popupWrapper) {
				var newValue = this.value;

				_.defer(function(self) {
					// check both oldValue and newValue - it is possible that value is changing multiple times due to single
					// user action, so wait for last change before notifying
					if (!_.isEqual(oldValue, self.value) && _.isEqual(newValue, self.value)) {
						self.onChange(newValue);
					}
				}, this);
			}
		},

		/**
		 * Event handler for change in calendar dates
		 * @param {Date} start
		 * @param {Date} end
		 */
		_onChangeCalendarDates: function(start, end) {
			this._bGroupChange = true;

			if (this.selection != Selection.CUSTOM) {
				this.set("selection", Selection.CUSTOM);
			}

			this.set("dateStart", types.getStartOfDay(start));
			this.set("dateEnd", types.getStartOfDay(end));

			this._bGroupChange = false;
			this._updateValue();
		},

		/**
		 * Event handler for change in end date from text box
		 * @param {Date} value
		 */
		_onChangeDateEnd: function(value) {
			if (value) {
				if (!this._isDisabledDate(value) && !_compare(value, this.dateEnd)) {
					this.set("dateEnd", value);

					if (!this._bGroupChange) {
						this._calendar.updateRange(null, value);
					}
				}
			}
		},

		/**
		 * Event handler for change in start date from text box
		 * @param {Date} value
		 */
		_onChangeDateStart: function(value) {
			if (value) {
				if (this._isDisabledDate(value) && !_compare(value, this.dateStart)) {
					this.set("dateStart", value);

					if (!this._bGroupChange) {
						this._calendar.updateRange(value, null);
					}
				}
			}
		},

		/**
		 * Event handler for change in start time
		 */
		_onChangeTimeStart: function(value) {
			// invalid values come in as null
			if (!value) {
				return;
			}

			this.set("timeStart", value);

			if (this._allowTime()) {
				// update end time to be half-hour past new start time if less than start time, or set to end of day
				var maxTime = _getDefaultEndTime();

				// call _compare(this.timeEnd, maxTime) is broken and not working as intended, because this.timeEnd and maxTime being created by different functions:
				// maxTime being created by _getDefaultEndTime()
				// this.timeEnd being created by getEndOfDay()
				// while both these function create the same "TIME" they may create DATE in different time zones (and with different years) and then
				// comparison in function _compare() make no sense, as .getTime() used there consider DATE and not time
				// and dates for time zones with negative offsets will have different YEARS and comparison will always return false
				// so, pretty much this code never works in our time zone but generate bugs in time zones with positive offset (like china).
				// However, this code supposed to be useful for events similar to Outlook create new appointment, where we want default appointment time to be 30 minutes
				// So we have to preserve this code and distinguish different use cases and therefore we introduce bBatchFilter flag
				// For adhoc filters we dont need this because it uses different creation/initialization sequence - internal dojo one, which apply attributes 
				// in different order and for adhoc filter at this point _allowTime() will be false (which is also wrong), but these two bugs compensate each other.
				if ((value.getTime() >= this.timeEnd.getTime()) || (!this.bBatchFilter && _compare(this.timeEnd, maxTime))) {					
					var dt = new Date(value);

					dt.setMinutes(dt.getMinutes() + TIME_INCREMENT);

					if (dt.getTime() > maxTime.getTime()) {
						dt = maxTime;
					}

					this.set("timeEnd", dt);
				}
			}

			if (!this._bGroupChange) {
				this._calendar.updateRange();
			}
		},

		/**
		 * Event handler for change in end time
		 */
		_onChangeTimeEnd: function(value) {
			// invalid values come in as null
			if (!value) {
				return;
			}

			this.set("timeEnd", value);

			// update start time to half-hour before new end time if greater than end time, or set to start of day
			if (this._allowTime()) {
				var minTime = _getDefaultStartTime();

				if (value.getTime() < this.timeStart.getTime()) {
					var dt = new Date(value);

					dt.setMinutes(dt.getMinutes() - TIME_INCREMENT);

					if (dt.getTime() < minTime.getTime()) {
						dt = minTime;
					}

					this.set("timeStart", dt);
				}
			}

			if (!this._bGroupChange) {
				this._calendar.updateRange();
			}
		},

		/**
		 * Show calendar tab rather than list
		 */
		_onClickCalendar: function(evt) {
			var domNode = evt.target;
			var css = this.baseClass + "-active-tab";
			domClass.remove(domNode.nextSibling, css);
			domClass.add(domNode, css);
			html.hide(this._listContentNode);
			html.show(this._calendarContentNode);
		},

		/**
		 * Show list tab rather than calendar
		 */
		_onClickList: function(evt) {
			var domNode = evt.target;
			var css = this.baseClass + "-active-tab";
			domClass.remove(domNode.previousSibling, css);
			domClass.add(domNode, css);
			html.hide(this._calendarContentNode);
			html.show(this._listContentNode);

			// populate list if not already done
			var listNode = this._listContentNode;
			if (listNode.firstChild) {
				this._updateListPosition();
				this._updateListSelection();
			} else {
				this._updateList();
			}
		},

		/**
		 * Toggle class to display blank and non-blank menu options
		 */
		_setAllowBlanksAttr: function(value) {
			this._set("allowBlanks", value);
			_updateMenuVisibility(this._menu, "blanks", value);
		},

		/**
		 * Toggle class to display past menu options
		 */
		_setAllowTimeAttr: function(value) {
			this._set("allowTime", value);
			html.setVisible(this._bottomNode, !!value);
		},

		/**
		 * Update calendar with new end date
		 * @param {Date} value
		 */
		_setDateEndAttr: function(value) {
			if (!_compare(value, this.dateEnd)) {
				this._set("dateEnd", value);

				if (this.dateStart && value && (this.dateStart.getTime() > value.getTime())) {
					this.set("dateStart", value);
				} else {
					this._updateValue();
				}

				this._calendar.set("endDate", value);
				this._endDateBox.set("value", value);
			}
		},

		/**
		 * Update calendar with new start date
		 * @param {Date} value
		 */
		_setDateStartAttr: function(value) {
			if (!_compare(value, this.dateStart)) {
				this._set("dateStart", value);

				if (this.dateEnd && value && (this.dateEnd.getTime() < value.getTime())) {
					this.set("dateEnd", value);
				} else {
					this._updateValue();
				}

				this._calendar.set("startDate", value);
				this._startDateBox.set("value", value);
			}
		},

		/**
		 * Toggle class to display past menu options
		 */
		_setInPastAttr: function(value) {
			this._set("inPast", value);
			_updateMenuVisibility(this._menu, "past", value);
		},

		/**
		 * Update widget with new end time
		 * @param {Date} value
		 */
		_setTimeEndAttr: function(value) {
			if (!_compare(value, this.timeEnd)) {
				this._set("timeEnd", value);
				this._updateValue();
				this._endTimeBox.set("value", value);
			}
		},

		/**
		 * Update widget with new start time
		 * @param {Date} value
		 */
		_setTimeStartAttr: function(value) {
			if (!_compare(value, this.timeStart)) {
				this._set("timeStart", value);
				this._updateValue();
				this._startTimeBox.set("value", value);
			}
		},

		/**
		 * Update widget based on menu selection
		 * @param {Selection} value
		 */
		_setSelectionAttr: function(value) {
			var bGroupChange = this._bGroupChange;
			var bReset = false;

			// these must be null in reset case to clear date text boxes
			var dtStart = null;
			var dtEnd = null;

			switch (value) {
				case Selection.ALL:
				case Selection.BLANKS:
				case Selection.NONBLANKS: {
					// reset dates when selecting All
					bReset = true;
					break;
				}

				case Selection.TODAY: {
					dtStart = types.getStartOfDay();
					dtEnd = types.getEndOfDay(dtStart);
					break;
				}

				case Selection.PAST_24_HOURS: {
					dtEnd = new Date();
					dtStart = new Date(dtEnd);
					dtStart.setDate(dtStart.getDate() - 1);
					break;
				}

				case Selection.PAST_WEEK: {
					dtEnd = types.getStartOfDay();
					dtStart = types.getStartOfDay();
					dtStart.setDate(dtStart.getDate() - 7);
					break;
				}

				case Selection.PAST_MONTH: {
					dtEnd = types.getStartOfDay();
					dtStart = types.getStartOfDay();
					dtStart.setMonth(dtStart.getMonth() - 1);
					break;
				}
			}

			this._bGroupChange = true;
			this._set("selection", value);
			_updateMenuSelection(this._menu, value);

			if (bReset || dtEnd) {
				this.set("dateEnd", dtEnd);
				this.set("timeEnd", dtEnd);
			}

			if (bReset || dtStart) {
				this.set("dateStart", dtStart);
				this.set("timeStart", dtStart);
			}

			// update value now that all changes are made
			this._bGroupChange = bGroupChange;
			this._updateValue();

			// move calendar to start or end of range, trying to keep the calendar from jumping months unnecessarily
			this._calendar.updateRange(dtStart, dtEnd);

			// show date text boxes when in custom mode
			html.setVisible(this._customDatesNode, value == Selection.CUSTOM);
		},

		/**
		 * Update widget based on if store exists
		 */
		_setStoreAttr: function(value) {
			this._set("store", value);

			if (value) {
				html.show(this._listContentNode);
			} else {
				html.hide(this._listContentNode);
			}

			if (value && this._list) {
				this._updateList();
			}
		},

		/**
		 * Update widget based on new value
		 * @param {number|number[]} value
		 */
		_setValueAttr: function(value) {
			if (this._onChangeActive && _.isEqual(value, this.value)) {
				return;
			}

			var bGroupChange = this._bGroupChange;
			var selection = Selection.ALL;
			var timeStart = _getDefaultStartTime();
			var timeEnd = _getDefaultEndTime();
			var dtStart = null;
			var dtEnd = null;

			if (value && _.isNumber(value)) {
				// handle single time value, such as when switching custom view filter operator; ignore time and reset to full day
				dtStart = types.getStartOfDay(value);
				dtEnd = types.getStartOfDay(value);
				selection = Selection.CUSTOM;
			} else if (value && (value.length >= 2)) {
				// if selection is preserved, then update date range to current dates if selection is relative
				if (value.length == 3) {
					selection = value[2];
					value = types.updateDateRange(value);
				} else {
					selection = Selection.CUSTOM;
				}

				// handle date range, taking care to preserve time specified in dates (in case it came from another time zone)
				dtStart = types.getStartOfDay(value[0]);
				dtEnd = types.getStartOfDay(value[1]);
				timeStart = types.combineDateAndTime(0, new Date(value[0]));
				timeEnd = types.combineDateAndTime(0, new Date(value[1]));

				// migrate DAY selection to CUSTOM
				if (selection == Selection.DAY) {
					selection = Selection.CUSTOM;
				}
			}

			this._bGroupChange = true;
			this.set("dateStart", dtStart);
			this.set("dateEnd", dtEnd);
			this.set("selection", selection);
			this.set("timeStart", timeStart);
			this.set("timeEnd", timeEnd);
			this._bGroupChange = bGroupChange;

			this._updateValue();
			this._updateFocus();
		},

		/**
		 * Ensure that calendar is focused on current date and redrawn
		 */
		_updateFocus: function() {
			this._calendar.updateRange(this.dateStart || new Date(), this.dateEnd, true);
		},

		/**
		 * Create/update list with values from current store
		 */
		_updateList: function() {
			var self = this;
			var list = this._list;
			var store = this.store;

			if (!store) {
				// delete list if no store
				if (list) {
					list.destroy();
					this._list = null;
				}
			} else if (this._listRendering) {
				// stop list from rendering twice
				return;
			} else if (list) {
				// list already exists, so just update store
				list.set("store", store, this.query);
			} else {
				// create list and listen for render and select events
				this._listRendering = true;

				list = new _List({
					className: "csi-dgrid-list",
					query: this.query,
					store: store,
					onSelectNew: function(value) {
						self._onChangeCalendarDates(new Date(value), new Date(value));
						self._updateFocus();
					},
					onSelectRangeStart: function(value) {
						self._onChangeDateStart(new Date(value));
					},
					onSelectRangeEnd: function(value) {
						self._onChangeDateEnd(new Date(value));
					}
				});

				this.own(aspect.after(list, "renderArray", function(results) {
					// ensure that when new results are rendered in list due to infinite scrolling that they are appropriately selected
					when(results, function() {
						if (!self._list) {
							self._list = list;
							self._listRendering = false;
							self._updateListPosition();
						}

						self._updateListSelection();
					});

					return results;
				}));

				this._listContentNode.appendChild(list.domNode);
				list.startup();
			}
		},

		/**
		 * Scroll to appropriate position in list
		 */
		_updateListPosition: function() {
			var list = this._list;
			if (!list) {
				return;
			}

			var queryObject = this.query;
			var store = this.store;
			var idProperty = store.idProperty;

			function findItemsInRange(start, end, range) {
				var t1 = start.getTime();
				var t2 = end.getTime();
				var before;
				var after;

				range = range || store.query(queryObject);

				var matches = _.filter(range, function(item) {
					var value = item[idProperty];
					if (value < t1) {
						before = item;
						return false;
					} else if ((value > t2) && !after) {
						after = item;
						return false;
					}

					return value <= t2;
				});

				if (matches.length) {
					return matches;
				} else if (after) {
					return [after];
				}

				return before ? [before] : [];
			}

			// find items in current month
			var dateStartCalendar = types.getStartOfDay(this._calendar.getFirstDate());
			var dateEndCalendar = new Date(dateStartCalendar);
			dateEndCalendar.setDate(dateEndCalendar.getDate() + DAYS_DISPLAYED_IN_CALENDAR);

			var items = findItemsInRange(dateStartCalendar, dateEndCalendar);
			if (!items.length) {
				return;
			}

			// now find item in month closest to currentFocus
			var dt = types.getStartOfDay(this._calendar.get("currentFocus"));
			var dt2 = new Date(dt);
			dt2.setDate(dt2.getDate() + 1);

			var currentFocusItems = findItemsInRange(dt, dt2, items);

			// if row exists for currently focused item, then just scroll it into view
			var itemToScroll = currentFocusItems[0];
			var row = list.row(itemToScroll);
			if (row && row.element) {
				win.scrollIntoView(row.element);
				return;
			}

			// if row does not exist, then use list scrollTo method to render and scroll to it
			var index = _.indexOf(items, itemToScroll);
			if (index != -1) {
				itemToScroll = items[Math.max(0, index - DAYS_DISPLAYED_IN_LIST + 1)];
			}

			list.scrollTo({y: itemToScroll[store.indexProperty] * list.get("rowHeight")});
		},

		/**
		 * Scroll to appropriate position in list
		 */
		_updateListSelection: function() {
			var list = this._list;
			if (!list) {
				return;
			}

			list.clearSelection(null, true);

			var dateStart = this.dateStart;
			if (dateStart) {
				var store = this.store;
				var idProperty = store.idProperty;
				var start = dateStart.getTime();
				var end = this.dateEnd.getTime();
				var startItem;
				var endItem;

				_.some(store.query(), function(item) {
					if (!startItem && (start <= item[idProperty]) && (item[idProperty] <= end)) {
						startItem = item;
					}

					if (startItem && (item[idProperty] <= end)) {
						// start tracking end item once start item is found
						endItem = item;
					} else if (item[idProperty] > end) {
						// stop traversing if found end item
						return true;
					}

					return false;
				});

				if (startItem) {
					list.select(startItem, (startItem == endItem) ? null : endItem);
				}
			}
		},

		/**
		 * Update value of widget with begin and end times of date range, along with selection and notify others if any change
		 */
		_updateValue: function() {
			// do nothing if all changes are not yet processed
			if (this._bGroupChange) {
				return;
			}

			// update time values and widgets based on dates selected. If start and end dates are the same, then user is allowed to enter time values.
			this._startTimeBox.set("disabled", !this._enableTime());
			this._endTimeBox.set("disabled", !this._enableTime());

			// if selection does not allow time, or time is not yet initialized, then reset to defaults
			if (!this._allowTime() || !this.timeStart) {
				this.set("timeStart", _getDefaultStartTime());
				this.set("timeEnd", _getDefaultEndTime());
			}

			// update value based on dates and times
			var dtStart;
			var dtEnd;

			if (this.dateStart && this.dateEnd && this.timeStart && this.timeEnd) {
				dtStart = types.combineDateAndTime(this.dateStart, this.timeStart);
				dtEnd = types.combineDateAndTime(this.dateEnd, this.timeEnd);
			}

			var oldValue = this.value;
			var newValue = types.getDateRange(dtStart, dtEnd, this.selection);

			this._set("value", newValue);
			this._updateListSelection();
			this._notifyOfChange(oldValue);
		}
	});
});